import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Mail, PlayCircle, Calendar, Eye, Clock } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function BoardDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);

  const loadCfg = useCallback(async () => {
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/admin/board-digest/config"),
        api.get("/admin/board-digest/log", { params: { limit: 8 } }),
      ]);
      setCfg(c.config);
      setLog(l.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load board digest config");
    }
  }, []);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/board-digest/config", patch);
      setCfg(data.config);
      toast.success("Digest settings saved");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/board-digest/run-now", null, {
        params: { property_id: "all", include_admins: true },
      });
      if (!data.recipients) {
        toast.warning("Digest fired but no board members were on file. Invite directors from the Board Members card above.");
      } else {
        toast.success(`Digest sent to ${data.sent.length} of ${data.recipients} recipient${data.recipients === 1 ? "" : "s"}`);
      }
      await loadCfg();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const previewInNewTab = async () => {
    try {
      const { data } = await api.get("/admin/board-digest/preview", { params: { property_id: "all" } });
      const blob = new Blob([data.html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Preview failed");
    }
  };

  if (!cfg) return null;

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="board-digest-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline">Automations</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <Mail className="h-4 w-4 text-indigo-400" /> Weekly Board Digest
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Every Monday morning, email every seated director a compact KPI + AR delta versus last week.
          </div>
        </div>
        <Switch
          checked={!!cfg.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          disabled={busy}
          data-testid="digest-enabled-switch"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <Label className="text-xs text-zinc-400 mb-1 inline-flex items-center gap-1">
            <Calendar className="h-3 w-3" /> Day of week (UTC)
          </Label>
          <Select
            value={String(cfg.day_of_week)}
            onValueChange={(v) => save({ day_of_week: Number(v) })}
          >
            <SelectTrigger className="bg-white/[0.04] border-white/10 h-9" data-testid="digest-day-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              {DAYS.map((d, i) => (
                <SelectItem key={i} value={String(i)}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400 mb-1 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> Hour (UTC 0-23)
          </Label>
          <Input
            type="number"
            min={0}
            max={23}
            value={cfg.hour_utc}
            onChange={(e) => setCfg({ ...cfg, hour_utc: Number(e.target.value) })}
            onBlur={(e) => save({ hour_utc: Number(e.target.value) })}
            className="bg-white/[0.04] border-white/10 h-9"
            data-testid="digest-hour-input"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-400 mb-1">Delivery mode</Label>
          <Select
            value={cfg.mode || "per_director"}
            onValueChange={(v) => save({ mode: v })}
          >
            <SelectTrigger className="bg-white/[0.04] border-white/10 h-9" data-testid="digest-mode-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              <SelectItem value="per_director">One email per director (stacks every community they sit on)</SelectItem>
              <SelectItem value="per_community">One email per community (legacy)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs text-zinc-400">Include admins on the digest</Label>
        <div className="mt-1 flex items-center">
          <Switch
            checked={!!cfg.include_admins}
            onCheckedChange={(v) => save({ include_admins: v })}
            disabled={busy}
            data-testid="digest-include-admins"
          />
          <span className="ml-2 text-xs text-zinc-500">CC every admin on the digest so property managers keep the paper trail.</span>
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-xs text-zinc-400 mb-1">Intro copy (variables: <code>{"{community}"}</code>, <code>{"{dashboard_link}"}</code>)</Label>
        <Textarea
          rows={3}
          value={cfg.note_template}
          onChange={(e) => setCfg({ ...cfg, note_template: e.target.value })}
          onBlur={(e) => save({ note_template: e.target.value })}
          className="bg-white/[0.04] border-white/10 text-sm"
          data-testid="digest-note-template"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button onClick={runNow} disabled={busy}
                className="bg-indigo-500 hover:bg-indigo-600 rounded-full"
                data-testid="digest-run-now-btn">
          <PlayCircle className="h-4 w-4 mr-1" /> Run digest now
        </Button>
        <Button variant="outline" onClick={previewInNewTab}
                className="border-white/10 rounded-full"
                data-testid="digest-preview-btn">
          <Eye className="h-4 w-4 mr-1" /> Preview email
        </Button>
      </div>

      {log.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Recent runs</div>
          <div className="space-y-1">
            {log.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-md border border-white/5 px-3 py-1.5 text-xs" data-testid={`digest-run-row-${r.id}`}>
                <div className="min-w-0">
                  <span className="text-zinc-300 truncate">{r.community_name}</span>
                  <span className="text-zinc-500 mx-1">·</span>
                  <span className="text-zinc-500">{new Date(r.at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-300">
                    {(r.sent || []).length} sent
                  </Badge>
                  {(r.failed || []).length > 0 && (
                    <Badge variant="outline" className="border-red-500/30 text-red-300">
                      {r.failed.length} failed
                    </Badge>
                  )}
                  <span className="text-zinc-500">by {r.actor}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

export default BoardDigestCard;
